import { maskSsnLikeInText } from './ryn-replay-masking-policy';
import {
  RYN_REPLAY_REDACTED,
  scrubRynReplayNetworkUrl,
  scrubRynReplayUrlsInText,
} from './ryn-replay-url-scrub';

export type RynReplayConsoleScrubMode = 'privacy' | 'diagnostic';
export const RYN_REPLAY_CONSOLE_SCRUB_MAX_DEPTH = 6;

const scrubConsoleString = (
  value: string,
  mode: RynReplayConsoleScrubMode
): string =>
  mode === 'diagnostic'
    ? maskSsnLikeInText(scrubRynReplayUrlsInText(value))
        .replace(/\b(Bearer\s+)[^\s,;"']+/gi, `$1${RYN_REPLAY_REDACTED}`)
        .replace(
          /\b((?:access|refresh|id)[_-]?token|password|authorization|secret|api[_-]?key)\s*[:=]\s*["']?[^\s,;"'}]+/gi,
          `$1=${RYN_REPLAY_REDACTED}`
        )
        .replace(/\b[A-Za-z0-9_-]{32,}\b/g, RYN_REPLAY_REDACTED)
    : RYN_REPLAY_REDACTED;

/** Shared privacy boundary for web and mobile Console envelopes. */
export const scrubRynReplayConsoleArg = (
  value: unknown,
  depth = 0,
  seen?: WeakSet<object>,
  mode: RynReplayConsoleScrubMode = 'privacy'
): unknown => {
  try {
    if (typeof value === 'string') {
      return scrubConsoleString(value, mode);
    }
    if (
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value === null ||
      value === undefined
    ) {
      return value;
    }
    if (value instanceof Error) {
      if (mode !== 'diagnostic') {
        return { name: value.name, message: RYN_REPLAY_REDACTED };
      }
      return {
        name: scrubConsoleString(value.name, mode),
        message: scrubConsoleString(value.message, mode),
        ...(value.stack
          ? { stack: scrubConsoleString(value.stack, mode) }
          : {}),
      };
    }
    if (depth >= RYN_REPLAY_CONSOLE_SCRUB_MAX_DEPTH) {
      return RYN_REPLAY_REDACTED;
    }
    if (typeof value === 'object') {
      const seenSet = seen ?? new WeakSet<object>();
      if (seenSet.has(value)) {
        return RYN_REPLAY_REDACTED;
      }
      seenSet.add(value);

      if (Array.isArray(value)) {
        return value.map((entry) =>
          scrubRynReplayConsoleArg(entry, depth + 1, seenSet, mode)
        );
      }

      const out: Record<string, unknown> = {};
      for (const [key, entry] of Object.entries(
        value as Record<string, unknown>
      )) {
        if (/(?:token|password|authorization|secret|key)/i.test(key)) {
          out[key] = RYN_REPLAY_REDACTED;
          continue;
        }
        if (
          mode === 'diagnostic' &&
          key.toLowerCase() === 'url' &&
          typeof entry === 'string'
        ) {
          out[key] = scrubRynReplayNetworkUrl(entry);
          continue;
        }
        out[key] = scrubRynReplayConsoleArg(entry, depth + 1, seenSet, mode);
      }
      return out;
    }
    return RYN_REPLAY_REDACTED;
  } catch {
    return RYN_REPLAY_REDACTED;
  }
};
